import React from "react";
import { useDispatch } from "react-redux";

import { DownloadIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Heading,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Td,
	Text,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment/moment";
import { FiCalendar, FiDollarSign, FiDownload, FiFileText, FiInfo, FiTrash2, FiXCircle } from "react-icons/fi";

import { removePaymentAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { fullDateTimeFormat } from "../../utils/types";

function CombinedPaymentsRow(props) {
	const { payment, owner } = props;
	const { amount, issuedDate, currency, method, type, number, id, documents, paymentDirection } = payment;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isDeleteConfirmationOpen, onOpen: onDeleteConfirmationOpen, onClose: onDeleteConfirmationClose } = useDisclosure();
	const toast = useToast();
	const dispatch = useDispatch();

	// Color mode values
	const cardBg = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const handleGetDocument = (documentId, documentName) => {
		const payload = { ownerEmail: owner.email, documentId, paymentId: id, documentName };
		console.log(payload);
		API.put("get-owner-payment-document", payload)
			.then((r) => {
				console.log(r);
				window.open(r.data.url, "_blank");
			})
			.catch((e) => {
				console.log(e);
				toast({
					title: "Error encountered",
					description: e.response.data.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};
	const handleDeletePayment = () => {
		const payload = { ownerEmail: owner.email, paymentId: id };
		console.log(payload);
		//dispatch remove payment
		dispatch(removePaymentAsync(payload))
			.then((r) => {
				if (r.error) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success",
						description: "Payment deleted successfully",
						status: "success",
						duration: 9000,
						isClosable: true,
					});
				}
			})
			.catch((e) => {
				console.log(e);
			})
			.finally(() => {
				onDeleteConfirmationClose();
			});
	};

	const renderDeleteConfirmationModal = () => {
		return (
			<Modal isOpen={isDeleteConfirmationOpen} onClose={onDeleteConfirmationClose}>
				<ModalOverlay backdropFilter="blur(10px)" />
				<ModalContent borderRadius="xl" boxShadow="2xl">
					<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
						<HStack spacing={3}>
							<Icon as={FiTrash2} color="red.500" boxSize={6} />
							<VStack align="start" spacing={0}>
								<Heading size="md">Confirm Delete</Heading>
								<Text fontSize="sm" color="gray.500">
									This action cannot be undone
								</Text>
							</VStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6}>
						<Alert status="warning" borderRadius="md" mb={4}>
							<AlertIcon />
							<VStack align="start" spacing={1}>
								<AlertTitle fontSize="sm">Warning</AlertTitle>
								<AlertDescription fontSize="sm">Are you sure you want to delete this payment? This action cannot be undone.</AlertDescription>
							</VStack>
						</Alert>
					</ModalBody>

					<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
						<HStack spacing={3}>
							<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onDeleteConfirmationClose}>
								Cancel
							</Button>
							<Button colorScheme="red" leftIcon={<Icon as={FiTrash2} />} onClick={handleDeletePayment} px={8}>
								Delete
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	const renderViewDocumentsModal = () => {
		return (
			<Modal isOpen={isOpen} onClose={onClose} size="4xl">
				<ModalOverlay backdropFilter="blur(10px)" />
				<ModalContent borderRadius="xl" boxShadow="2xl" maxH="90vh">
					<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
						<HStack spacing={3}>
							<Icon as={FiFileText} color={iconColor} boxSize={6} />
							<VStack align="start" spacing={0}>
								<Heading size="md">Payment Details</Heading>
								<Text fontSize="sm" color="gray.500">
									View payment information and documents
								</Text>
							</VStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody py={6} overflowY="auto">
						<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
							{/* Left Column - Payment Information */}
							<VStack spacing={6} align="stretch">
								{/* Payment Summary Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiInfo} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Payment Summary
											</Text>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										<VStack spacing={4} align="stretch">
											<SimpleGrid columns={2} spacing={4}>
												<Box>
													<HStack spacing={2} mb={2}>
														<Icon as={FiDollarSign} color={iconColor} boxSize={4} />
														<Text fontSize="sm" fontWeight="medium">
															Amount
														</Text>
													</HStack>
													<Text fontSize="lg" fontWeight="bold">
														{paymentDirection === "outgoing" || type === "administrationFee" ? "-" : "+"}
														{amount} {currency}
													</Text>
												</Box>
												<Box>
													<HStack spacing={2} mb={2}>
														<Icon as={FiFileText} color={iconColor} boxSize={4} />
														<Text fontSize="sm" fontWeight="medium">
															Type
														</Text>
													</HStack>
													<Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
														{type}
													</Badge>
												</Box>
											</SimpleGrid>

											<Box>
												<HStack spacing={2} mb={2}>
													<Icon as={FiFileText} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="medium">
														Method
													</Text>
												</HStack>
												<Text fontSize="sm" fontWeight="medium">
													{method}
												</Text>
											</Box>

											{number && (
												<Box>
													<HStack spacing={2} mb={2}>
														<Icon as={FiFileText} color={iconColor} boxSize={4} />
														<Text fontSize="sm" fontWeight="medium">
															Document Number
														</Text>
													</HStack>
													<Text fontSize="sm" fontWeight="medium" fontFamily="mono" bg="gray.50" p={2} borderRadius="md">
														{number}
													</Text>
												</Box>
											)}
										</VStack>
									</CardBody>
								</Card>

								{/* Payment Timeline Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiCalendar} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Payment Timeline
											</Text>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										<VStack spacing={4} align="stretch">
											<Box>
												<HStack spacing={2} mb={2}>
													<Icon as={FiCalendar} color={iconColor} boxSize={4} />
													<Text fontSize="sm" fontWeight="medium">
														Issued Date
													</Text>
												</HStack>
												<Text fontSize="sm" fontWeight="medium">
													{moment(issuedDate).format(fullDateTimeFormat)}
												</Text>
											</Box>
										</VStack>
									</CardBody>
								</Card>
							</VStack>

							{/* Right Column - Documents */}
							<VStack spacing={6} align="stretch">
								{/* Documents Card */}
								<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" flex={1}>
									<CardHeader pb={3}>
										<HStack spacing={3}>
											<Icon as={FiDownload} color={iconColor} boxSize={5} />
											<Text fontWeight="semibold" fontSize="md">
												Documents
											</Text>
											<Badge colorScheme="blue" fontSize="sm">
												{documents?.length || 0}
											</Badge>
										</HStack>
									</CardHeader>
									<CardBody pt={0}>
										{documents && documents.length > 0 ? (
											<VStack spacing={3} align="stretch">
												{documents.map((d, index) => (
													<Box
														key={index}
														p={3}
														border="1px"
														borderColor="gray.200"
														borderRadius="md"
														_hover={{ bg: "gray.50" }}
														cursor="pointer"
														onClick={() => handleGetDocument(d.id, d.documentName)}
													>
														<HStack justify="space-between">
															<HStack spacing={3}>
																<Icon as={FiFileText} color={iconColor} boxSize={4} />
																<VStack align="start" spacing={0}>
																	<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
																		{d.documentName}
																	</Text>
																	<Text fontSize="xs" color="gray.500">
																		Click to download
																	</Text>
																</VStack>
															</HStack>
															<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
														</HStack>
													</Box>
												))}
											</VStack>
										) : (
											<Box textAlign="center" py={6}>
												<Icon as={FiFileText} color="gray.300" boxSize={8} mb={3} />
												<Text fontSize="sm" color="gray.500">
													No documents attached
												</Text>
											</Box>
										)}
									</CardBody>
								</Card>
							</VStack>
						</SimpleGrid>
					</ModalBody>

					<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
						<HStack spacing={3}>
							<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onClose}>
								Close
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	};
	return (
		<Tr>
			<Td minWidth={{ sm: "150px" }} pl="0px">
				<Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
					{/* <Avatar src={logo} w="50px" borderRadius="12px" me="18px" /> */}
					<Flex direction="column">
						<Text fontSize="md" minWidth="100%">
							{moment(issuedDate).format(fullDateTimeFormat)}
						</Text>
						<Text fontSize="sm" fontWeight="normal" minWidth="100%" hidden={!number}>
							Document Number: {number}
						</Text>
					</Flex>
				</Flex>
			</Td>
			<Td>
				<Text>
					{paymentDirection === "outgoing" || type === "administrationFee" ? "-" : "+"}
					{amount} {currency}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md">{method}</Text>
			</Td>
			<Td>
				<Text fontSize="md">{type}</Text>
			</Td>

			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						<MenuItem onClick={onOpen}>View Details</MenuItem>
						{/* <MenuItem onClick={onDeleteConfirmationOpen}>Delete</MenuItem> */}
					</MenuList>
				</Menu>
				{renderViewDocumentsModal()}
				{renderDeleteConfirmationModal()}
			</Td>
		</Tr>
	);
}

export default CombinedPaymentsRow;
